import { b64uDecode, b64uEncode, concatBytes, utf8Decode, utf8Encode } from "./utils";

function buildNonce(counter: number, randomSuffix?: Uint8Array): Uint8Array {
  if (counter <= 0) throw new Error("counter must be > 0");
  if (randomSuffix && randomSuffix.length !== 4) throw new Error("randomSuffix must be 4 bytes");
  const nonce = new Uint8Array(12);
  const view = new DataView(nonce.buffer);
  view.setBigUint64(0, BigInt(counter), false);
  if (randomSuffix) nonce.set(randomSuffix, 8);
  else crypto.getRandomValues(nonce.subarray(8));
  return nonce;
}

function extractCounter(nonce: Uint8Array): number {
  return Number(new DataView(nonce.buffer, nonce.byteOffset, 12).getBigUint64(0, false));
}

let webCryptoKeyCache: { raw: Uint8Array; key: CryptoKey } | null = null;
async function importKey(raw: Uint8Array): Promise<CryptoKey> {
  if (webCryptoKeyCache && webCryptoKeyCache.raw === raw) return webCryptoKeyCache.key;
  const key = await crypto.subtle.importKey("raw", raw, { name: "AES-GCM" }, false, [
    "encrypt",
    "decrypt",
  ]);
  webCryptoKeyCache = { raw, key };
  return key;
}

export async function sealEnvelopeBytes(
  sessionKey: Uint8Array,
  counter: number,
  plaintext: Uint8Array,
  opts: { nonceRandomSuffix?: Uint8Array } = {},
): Promise<Uint8Array> {
  const nonce = buildNonce(counter, opts.nonceRandomSuffix);
  const key = await importKey(sessionKey);
  const ctBuf = await crypto.subtle.encrypt({ name: "AES-GCM", iv: nonce, tagLength: 128 }, key, plaintext);
  const ct = new Uint8Array(ctBuf);
  const payload = concatBytes(nonce, ct);
  const envelopeText = JSON.stringify({ d: b64uEncode(payload) });
  return utf8Encode(envelopeText);
}

export async function sealEnvelopeText(
  sessionKey: Uint8Array,
  counter: number,
  plaintext: string,
  opts: { nonceRandomSuffix?: Uint8Array } = {},
): Promise<string> {
  const out = await sealEnvelopeBytes(sessionKey, counter, utf8Encode(plaintext), opts);
  return utf8Decode(out);
}

export async function openEnvelopeBytes(
  sessionKey: Uint8Array,
  envelopeBytes: Uint8Array,
): Promise<{ plaintext: Uint8Array; counter: number }> {
  const env = JSON.parse(utf8Decode(envelopeBytes)) as { d?: string };
  if (!env.d) throw new Error("envelope missing d");

  const payload = b64uDecode(env.d);
  if (payload.length < 12 + 16) throw new Error("envelope too short");

  const nonce = payload.slice(0, 12);
  const ct = payload.slice(12);

  const key = await importKey(sessionKey);
  const plainBuf = await crypto.subtle.decrypt({ name: "AES-GCM", iv: nonce, tagLength: 128 }, key, ct);
  return { plaintext: new Uint8Array(plainBuf), counter: extractCounter(nonce) };
}
